import React from 'react'
import styles from './MicrositeFooterSectionForm.module.css'
import TextInput from '../../../components/TextInput/TextInput'

// ──────────────────────────────────────────────────────────────
// Default skeleton matching the real production content shape.
// Note: there's no logo, tagline, copyright text, or repeatable
// links array in the real shape — the footer's nav links render
// from osm_store_config_nav directly, and the logo comes from
// theme.logoUrl. This section only controls the three column
// headings shown above the footer's nav/social/location blocks,
// plus a short description paragraph.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    description: '',
    navigationTitle: 'Navigation',
    connectTitle: 'Connect',
    locationTitle: 'Location'
}

// Backfills any missing key so older or partially-saved content
// still renders without crashing.
const withDefaults = (content) => ({
    description: content?.description ?? DEFAULT_CONTENT.description,
    navigationTitle: content?.navigationTitle ?? DEFAULT_CONTENT.navigationTitle,
    connectTitle: content?.connectTitle ?? DEFAULT_CONTENT.connectTitle,
    locationTitle: content?.locationTitle ?? DEFAULT_CONTENT.locationTitle
})

/**
 * MicrositeFooterSectionForm
 * Content editor for the landing page "Footer" section, matching
 * the real production content shape: a short description paragraph
 * plus three column heading labels (Navigation / Connect /
 * Location). There is no logo, tagline, copyright text, or
 * repeatable links array here — the footer's actual nav links come
 * from osm_store_config_nav (edited on the Navigation screen), the
 * logo comes from theme.logoUrl (edited on the Theme screen), and
 * the location block renders from the store's address fields.
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes required: osm_store_config_landing.content is
 * a generic JSON column already; this form just sends the matching
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeFooterSectionForm = ({ content, onChange }) => {
    const safe = withDefaults(content)

    const update = (field, value) => onChange?.({ ...safe, [field]: value })

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>Footer</h2>
            <p className={styles.groupDesc}>
                Shown at the bottom of every page. Nav links, logo, and address come from their own settings screens — this section only covers the description and column headings.
            </p>

            <label className={styles.textareaLabel}>Description</label>
            <textarea
                className={styles.textarea}
                rows={4}
                value={safe.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="e.g. Lokaci is India's leading platform for salons, providing an all-in-one operating system to manage talent, operations, and procurement at scale."
            />

            <h3 className={styles.subTitle}>Column headings</h3>
            <div className={styles.row3}>
                <TextInput
                    label="Navigation column title"
                    value={safe.navigationTitle}
                    onChange={(v) => update('navigationTitle', v)}
                    placeholder="Navigation"
                />
                <TextInput
                    label="Connect column title"
                    value={safe.connectTitle}
                    onChange={(v) => update('connectTitle', v)}
                    placeholder="Connect"
                />
                <TextInput
                    label="Location column title"
                    value={safe.locationTitle}
                    onChange={(v) => update('locationTitle', v)}
                    placeholder="Location"
                />
            </div>
        </section>
    )
}

export default MicrositeFooterSectionForm